/**
 * Breadcrumbs for logger
 *
 * Provides a function to set logging breadcrumbs for DOM events.
 * Events are set on the document.
 *
 * This dom event listener is global for a single page load
 */
const BREADCRUMB_BUFFER_SIZE = 5;
const BREADCRUMB_STRING_CHAR_LIMIT = 250;

interface BreadcrumbBuffer {
  getBreadcrumbsString(): string;
  push(value: string): void;
}

/**
 * Eviction queue for breadcrumbs
 *
 * @param {number} size array buffer length
 */
export function breadcrumbBufferFactory(
  size: number = BREADCRUMB_BUFFER_SIZE
): BreadcrumbBuffer {
  const crumbs: string[] = [];

  return {
    getBreadcrumbsString: () => getBreadcrumbString(crumbs),
    push(value) {
      crumbs.push(value);
      if (crumbs.length > size) {
        crumbs.shift();
      }
    },
  };
}

/**
 * Retrieve a string from breadcrumbs buffer for logging
 * Limit to 250 chars, cutting off least recent crumbs
 *
 * @param {*} crumbs
 */
function getBreadcrumbString(crumbs: string[]): string {
  const breadcrumbStr = crumbs.join(' > ');
  const breadcrumbsLength = breadcrumbStr.length;
  return (
    (breadcrumbsLength > BREADCRUMB_STRING_CHAR_LIMIT ? '...' : '') +
    breadcrumbStr.substring(
      breadcrumbsLength - BREADCRUMB_STRING_CHAR_LIMIT,
      breadcrumbsLength
    )
  );
}

/**
 *  Init function to set a global event listener for adding event breadcrumbs to JS logger
 *
 * @param eventName
 * @param breadcrumbBuffer
 */
export function initDOMEventBreadcrumb({
  eventName,
  breadcrumbBuffer,
}: {
  eventName: string;
  breadcrumbBuffer: BreadcrumbBuffer;
}): void {
  // Event listener should be set only once, and only clientside
  if (typeof window !== 'undefined' && window.document.addEventListener) {
    window.document.addEventListener(
      eventName,
      ({target}) => {
        // Check that the event target is an HTMLElement
        // so we can make assumptions about properties it will contain
        // e.g., (id, tagName)
        if (target instanceof HTMLElement) {
          let message = `${eventName}:${target.tagName.toLowerCase()}`;
          if (target.id) {
            message += `#${target.id}`;
          }
          if (target.className) {
            message += `.${target.className.replace(/\s+/g, '.')}`;
          }
          breadcrumbBuffer.push(message);
        }
      },
      false
    );
  }
}

export function initBreadcrumbBuffer(events: string[]): BreadcrumbBuffer {
  // initalize breadcrumbs for this logger
  const breadcrumbBuffer = breadcrumbBufferFactory();
  if (events.length > 0) {
    // initalize breadcrumb buffer or retrieve existing
    events.forEach(eventName => {
      // init the DOM event listener, will not run on server
      initDOMEventBreadcrumb({
        eventName,
        breadcrumbBuffer,
      });
    });
  }

  return breadcrumbBuffer;
}
